import * as React from 'react';
import { useCallback } from 'react';
import { Collapse } from '@mui/material';
import cn from 'classnames';

import { CheckinOrderFragment, CheckoutOrderFragment } from '@websky/graphql';
import { Steps } from '../../BaseComponents/Stepbar/types';
import { CheckoutSteps } from '../types';
import { ServiceSummaryType } from '../../ServiceSummary/types';
import { OnDeleteItemType } from '../../Cart/types';
import { ThemeProvider, useTheme } from '../../theme';
import { useCartPrices } from '../../Cart/hooks';
import { useMobileStepbar } from '../hooks';
import Cart from '../../Cart/Cart';
import StepbarHeader from './StepbarHeader/StepbarHeader';
import StepbarServiceSummary from './StepbarServiceSummary/StepbarServiceSummary';
import Dropdown from '../../Dropdown';
import { STEP_ICON_NAME_BY_TYPE } from '../../BaseComponents/Stepbar/Icons';
import { useConfig } from '../../context';
import { CdnIcon } from '@shared/ui';

export interface Props {
	order: CheckoutOrderFragment | CheckinOrderFragment;
	currentStep: number;
	stepbarConfig: Steps;
	alwaysOpen?: boolean;
	hideFlightPrice?: boolean;
	onServiceDelete?: OnDeleteItemType;
}

const MobileStepbar: React.FC<Props> = ({
	alwaysOpen,
	order,
	currentStep,
	stepbarConfig,
	hideFlightPrice,
	onServiceDelete
}) => {
	const { MobileStepbarStyles: css } = useTheme('MobileStepbar');

	const { stepsAvailable } = useConfig().global;

	const cartPrices = useCartPrices();

	const {
		isOpen,
		toggle,
		close,
		cartServiceProps,
		cartServices,
		availableSteps,
		additionalSteps,
		isBookedOrConfirmed,
		isCheckoutOrder,
		isCancelled,
		isPayment
	} = useMobileStepbar(order, stepbarConfig, alwaysOpen);

	const onClickStep = useCallback(
		(onClick: () => void) => () => {
			close();
			onClick();
		},
		[isOpen]
	);

	const renderService = useCallback(
		(type: CheckoutSteps, group: ServiceSummaryType, content: React.ReactNode) => {
			let actualType = type;
			let serviceSummary = group;

			const isExtras = actualType === CheckoutSteps.Extras;

			// hide extras for checkout order
			if (isExtras && isCheckoutOrder && !content) {
				return null;
			}

			const isPassengers = actualType === CheckoutSteps.Passengers;
			const isOrderBookedOrConfirmedStep = isPassengers && isCheckoutOrder && isBookedOrConfirmed;
			const isOrderCancelledStep = isPassengers && isCheckoutOrder && isCancelled;

			const isBookedOrderHasDisabledMealStep =
				isBookedOrConfirmed && stepsAvailable[CheckoutSteps.Meal] === false;

			// after booking or cancelled, the Passenger step essentially becomes the Order step, so we replace the type and information
			if (
				(isOrderBookedOrConfirmedStep || isOrderCancelledStep) &&
				!additionalSteps.includes(CheckoutSteps.Order)
			) {
				actualType = CheckoutSteps.Order;
				serviceSummary = {
					...serviceSummary,
					header: stepbarConfig[CheckoutSteps.Order]?.label ?? serviceSummary.header,
					icon: <CdnIcon icon={STEP_ICON_NAME_BY_TYPE[CheckoutSteps.Order]} />
				};
			}

			const stepIndex = availableSteps.findIndex(step => step.type === actualType);
			const stepByType = availableSteps[stepIndex];

			let serviceClick: () => void;

			if (stepByType?.onClick) {
				serviceClick = onClickStep(stepByType.onClick);
			}

			if (
				(isOrderBookedOrConfirmedStep ||
					isOrderCancelledStep ||
					isPayment ||
					(type === CheckoutSteps.Meal && isBookedOrderHasDisabledMealStep)) &&
				serviceClick
			) {
				serviceClick = null;
			}

			const isPassed = stepIndex < currentStep;
			const isActive = stepIndex === currentStep;
			const isSelected = isPassengers ? (isPassed || isActive) && !!content : !!content;

			return (
				<StepbarServiceSummary
					type={actualType}
					group={serviceSummary}
					isPassed={isPassed}
					isActive={isActive}
					isSelected={isSelected}
					onClick={serviceClick}
				>
					{content}
				</StepbarServiceSummary>
			);
		},
		[currentStep, availableSteps, additionalSteps, isBookedOrConfirmed, isCheckoutOrder, isPayment]
	);

	const renderServices = useCallback(() => {
		if (additionalSteps.length) {
			return additionalSteps.map(additionalStep => {
				const stepIndex = availableSteps.findIndex(step => step.type === additionalStep);
				const stepByType = availableSteps[stepIndex];

				return (
					<StepbarServiceSummary
						key={additionalStep}
						type={additionalStep}
						group={{
							header: stepByType?.label,
							icon: <CdnIcon icon={STEP_ICON_NAME_BY_TYPE[additionalStep]} />
						}}
						isPassed={stepIndex < currentStep}
						isActive={stepIndex === currentStep}
						isSelected={false}
					/>
				);
			});
		}

		return null;
	}, [currentStep, availableSteps, additionalSteps]);

	return (
		<Dropdown isOpen onClose={close}>
			<div className={css.stepbar}>
				<StepbarHeader
					currentStep={currentStep}
					steps={availableSteps}
					totalPrice={cartPrices.total}
					isOpen={isOpen}
					onToggle={!alwaysOpen ? toggle : null}
				/>

				<Collapse in={isOpen}>
					<ThemeProvider
						value={{
							Cart: {
								Cart: { wrapper: css.stepbar__cartWrapper }
							},
							ServiceSummary: {
								ServiceSummaryOption: {
									option__price_icon: cn({
										[css.stepbar_hidden]: isPayment
									})
								}
							}
						}}
					>
						<Cart
							{...cartServiceProps}
							prices={cartPrices}
							cartServices={cartServices}
							renderService={renderService}
							renderServices={renderServices}
							onServiceDelete={onServiceDelete}
							hideFlightPrice={hideFlightPrice}
							showPassengers
							hideToolbar
						/>
					</ThemeProvider>
				</Collapse>
			</div>
		</Dropdown>
	);
};

export default MobileStepbar;
